// ===========================================
// Absence display helpers (teacher-facing)
// ===========================================
// What a teacher is told about a reported absence: the CATEGORY and who
// reported it — never `reasonDetails` (Andrew D2, 2026-09-07; parents write
// medical detail there). The backend does not send the field; this file could
// not show it even by mistake.

import type { AbsenceReason, UserRole } from '../api/absenceNotification.api';
import type { RosterAbsence } from '../api/attendance.api';
import { formatUTCDate } from './dateUtils';

export const ABSENCE_REASON_LABELS: Record<AbsenceReason, string> = {
  ILLNESS: 'Illness',
  SCHOOL_CAMP: 'School camp',
  FAMILY_COMMITMENT: 'Family commitment',
  DOCTOR_APPOINTMENT: "Doctor's appointment",
  HOLIDAY: 'Holiday',
  OTHER: 'Other',
};

const REPORTED_BY: Record<UserRole, string> = {
  PARENT: 'parent',
  ADMIN: 'the school',
  TEACHER: 'a teacher',
  STUDENT: 'the student',
};

/** `dd/mm/yyyy` from a `@db.Date` value — UTC getters, per CLAUDE.md rule 7. */
const dmy = (value: string | Date) =>
  formatUTCDate(value, { day: '2-digit', month: '2-digit', year: 'numeric' });

/**
 * "Away — Illness, reported by parent" (+ " · 10/09/2026 – 14/09/2026" when the
 * absence spans more than one day).
 */
export function describeAbsence(absence: RosterAbsence): string {
  const label = ABSENCE_REASON_LABELS[absence.reason] ?? ABSENCE_REASON_LABELS.OTHER;
  const who = REPORTED_BY[absence.submittedByRole] ?? 'the school';
  const start = dmy(absence.startDate);
  const end = dmy(absence.endDate);
  const range = start !== end ? ` · ${start} – ${end}` : '';
  return `Away — ${label}, reported by ${who}${range}`;
}
